import { memo, Ref } from "react";

import { ShiftSelectionAction } from "components/BulkActions/useShiftSelectionManager";

import TableBodyRow from "../Table/Row";
import { TableCellPerColumn, TableSimpleDataItem, TableSimpleSelectionStore } from "./types";
import { isStickyLeft, isStickyRight } from "./helpers";
import { TableColumnMeta } from "../Table/types";

type TableSimpleRowProps<T extends TableSimpleDataItem> = {
  item: T;
  itemId: string;
  columns: TableColumnMeta[];
  isFirstColumnPinned: boolean;
  selectionStore?: TableSimpleSelectionStore;
  hasMoreSelectionOptions?: boolean;
  shiftSelectionAction?: ShiftSelectionAction;
  cellPerColumn: TableCellPerColumn<T>;
  keyboardProps: Record<string, unknown>;
  measureRef?: Ref<HTMLDivElement>;
  dataIndex?: number;
  virtualized?: boolean;
};

const TableSimpleRow = <T extends TableSimpleDataItem>({
  item,
  itemId,
  columns,
  isFirstColumnPinned,
  selectionStore,
  hasMoreSelectionOptions,
  shiftSelectionAction,
  cellPerColumn,
  keyboardProps,
  measureRef,
  dataIndex,
  virtualized,
}: TableSimpleRowProps<T>) => {
  return (
    <TableBodyRow {...keyboardProps} ref={measureRef} dataIndex={dataIndex} virtualized={virtualized}>
      {columns.map((column, i) => {
        const cellId = `${itemId}-${column.id}`;
        const Cell = cellPerColumn[column.id];
        const stickyLeft = isStickyLeft(i, isFirstColumnPinned);
        const stickyRight = isStickyRight(i, columns.length);
        const withSelection = i === 0 && selectionStore && itemId;

        return (
          <Cell
            key={cellId}
            id={cellId}
            stickyLeft={stickyLeft}
            stickyRight={stickyRight}
            item={item}
            {...(withSelection && {
              selectionId: itemId,
              selectionStore,
              hasMoreSelectionOptions,
              shiftSelectionAction,
            })}
          />
        );
      })}
    </TableBodyRow>
  );
};

TableSimpleRow.displayName = "DS.TableSimple.Row";

export default memo(TableSimpleRow) as typeof TableSimpleRow;
